import { stripMathText } from './math-content';

const TEXT_IGNORE_SELECTOR = [
  'script',
  'style',
  'noscript',
  'svg',
  'canvas',
  'audio',
  'video',
  'img',
  'picture',
  'source',
  'iframe',
  'object',
  'embed',
  'math',
  'annotation',
  'semantics',
  'input',
  'textarea',
  'select',
  'option',
  '.MathJax',
  '[class*="MathJax"]',
  'mjx-container',
  '[class*="mjx-"]',
  '.katex',
  '.katex-display',
  '.ql-formula',
  '[data-mathml]',
  '[data-latex]',
  '[data-exam-integrity-ignore]'
].join(',');

// Text nodes separated by block-level elements need a boundary, while inline
// elements must keep the exact surrounding text. Joining every text node with
// a space changes content such as `"<strong>Title</strong>"` into
// `" Title "` and creates false integrity warnings.
const TEXT_BOUNDARY_TAGS = new Set([
  'address',
  'article',
  'aside',
  'blockquote',
  'dd',
  'div',
  'dl',
  'dt',
  'fieldset',
  'figcaption',
  'figure',
  'footer',
  'form',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'header',
  'hr',
  'li',
  'main',
  'nav',
  'ol',
  'p',
  'pre',
  'section',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'ul'
]);

function normalizeWhitespace(text) {
  return (text || '')
    .replace(/[\u200b-\u200d\ufeff]/g, '')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}
export function normalizeIntegrityText(text) {
  return normalizeWhitespace(stripMathText(text))
    .normalize('NFKC')
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[‐‑‒–—]/g, '-')
    .replace(/\s+([,.;:!?])/g, '$1')
    .replace(/([([{])\s+/g, '$1')
    .replace(/\s+([)\]}])/g, '$1')
    .toLowerCase();
}

export function htmlToPlainText(html) {
  if (!html) {
    return '';
  }

  if (typeof document !== 'undefined') {
    const element = document.createElement('div');
    element.innerHTML = String(html);

    // Use the same DOM walker as the visible-page check. Maintaining a
    // separate set of closing-tag replacements here caused block boundaries
    // such as </h2><p> to be interpreted differently on the two sides.
    return normalizeWhitespace(collectIntegrityText(element, false));
  }

  return normalizeWhitespace(
    String(html)
      .replace(/&lt;br\s*\/?&gt;/gi, ' ')
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ')
  );
}

function isElementVisible(element) {
  let current = element;
  while (current && current.nodeType === 1) {
    const style = window.getComputedStyle(current);
    if (
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      style.opacity === '0' ||
      current.getAttribute('aria-hidden') === 'true'
    ) {
      return false;
    }
    current = current.parentElement;
  }
  return true;
}

function collectIntegrityText(node, visibleOnly) {
  if (!node) {
    return '';
  }

  if (node.nodeType === 3) {
    return node.nodeValue || '';
  }

  const isElement = node.nodeType === 1;
  if (isElement) {
    if (node.matches(TEXT_IGNORE_SELECTOR)) {
      return '';
    }
    if (visibleOnly && !isElementVisible(node)) {
      return '';
    }

    const tagName = node.tagName.toLowerCase();
    if (tagName === 'br' || tagName === 'hr') {
      return ' ';
    }
  }

  const text = Array.from(node.childNodes || [])
    .map((child) => collectIntegrityText(child, visibleOnly))
    .join('');

  if (isElement && TEXT_BOUNDARY_TAGS.has(node.tagName.toLowerCase())) {
    return ` ${text} `;
  }

  return text;
}

export function getVisibleText(root) {
  if (!root) {
    return '';
  }

  return normalizeWhitespace(collectIntegrityText(root, true));
}

function getComparableText(root) {
  if (!root) {
    return '';
  }

  // Expected HTML is parsed outside the live document, so CSS visibility is
  // not a reliable comparison boundary. Include hidden editor text on both
  // sides and continue excluding scripts, controls, formulas, and guard UI.
  return normalizeWhitespace(collectIntegrityText(root, false));
}

function findFirstDiffIndex(expectedText, actualText) {
  const length = Math.min(expectedText.length, actualText.length);
  for (let i = 0; i < length; i++) {
    if (expectedText[i] !== actualText[i]) {
      return i;
    }
  }
  return length;
}

function getDiffSample(text, index) {
  const start = Math.max(0, index - 40);
  return text.slice(start, start + 120);
}

export function compareVisibleTextWithExpected(root, expectedText, options = {}) {
  const label = options.label || 'Exam content';
  const expected = normalizeIntegrityText(expectedText);
  if (!root) {
    return {
      suspicious: false,
      inconclusive: true,
      reason: '',
      label,
      expectedLength: expected.length,
      actualLength: 0
    };
  }

  const actualRawText = getComparableText(root);
  const actual = normalizeIntegrityText(actualRawText);

  if (!expected && !actual) {
    return {
      suspicious: false,
      reason: '',
      label,
      expectedLength: 0,
      actualLength: 0
    };
  }

  if (!expected && actual) {
    return {
      suspicious: true,
      reason: 'extra_exam_content',
      label,
      expectedLength: 0,
      actualLength: actual.length,
      actualSample: actualRawText.slice(0, 160)
    };
  }

  if (expected && !actual) {
    // DOM can be briefly empty while Vue/MathJax finishes painting.
    // Treat as inconclusive so the guard can retry instead of false-alarming.
    return {
      suspicious: false,
      inconclusive: true,
      reason: 'missing_exam_content',
      label,
      expectedLength: expected.length,
      actualLength: 0,
      expectedSample: expectedText.slice(0, 160)
    };
  }

  if (actual === expected) {
    return {
      suspicious: false,
      reason: '',
      label,
      expectedLength: expected.length,
      actualLength: actual.length
    };
  }

  if (actual.includes(expected)) {
    return {
      suspicious: true,
      reason: 'extra_exam_content',
      label,
      expectedLength: expected.length,
      actualLength: actual.length,
      expectedSample: expectedText.slice(0, 160),
      actualSample: actualRawText.slice(0, 160)
    };
  }

  if (expected.includes(actual)) {
    return {
      suspicious: true,
      reason: 'missing_exam_content',
      label,
      expectedLength: expected.length,
      actualLength: actual.length,
      expectedSample: expectedText.slice(0, 160),
      actualSample: actualRawText.slice(0, 160)
    };
  }

  const diffIndex = findFirstDiffIndex(expected, actual);
  if (actual.length > expected.length) {
    return {
      suspicious: true,
      reason: 'extra_exam_content',
      label,
      expectedLength: expected.length,
      actualLength: actual.length,
      expectedSample: getDiffSample(expectedText, diffIndex),
      actualSample: getDiffSample(actualRawText, diffIndex)
    };
  }

  if (actual.length < expected.length) {
    return {
      suspicious: true,
      reason: 'missing_exam_content',
      label,
      expectedLength: expected.length,
      actualLength: actual.length,
      expectedSample: getDiffSample(expectedText, diffIndex),
      actualSample: getDiffSample(actualRawText, diffIndex)
    };
  }

  return {
    suspicious: true,
    reason: 'exam_content_mismatch',
    label,
    expectedLength: expected.length,
    actualLength: actual.length,
    expectedSample: getDiffSample(expectedText, diffIndex),
    actualSample: getDiffSample(actualRawText, diffIndex)
  };
}
